// react
import { useState } from 'react';

// mui
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';

// components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';
import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';

// models
import {
  BacktestTranscriptChoice,
  CreateBacktestRequest,
  EvaluationSource,
} from '@karehero/models';
import { formatDateTime } from '@karehero/helpers/datetime';

// data
import {
  ORIGIN_LABEL,
  transcriptLabel,
} from '@/pwa/pages/AdminBacktestsPage/data';

const readyTranscripts = (source?: EvaluationSource) =>
  (source?.transcripts ?? [])
    .filter((transcript) => transcript.status === 'ready')
    .sort((a, b) => b.requestedAt.localeCompare(a.requestedAt));

const sourceLabel = (source: EvaluationSource) =>
  `${source.label} (${ORIGIN_LABEL[source.origin]} ${source.originRef})`;

export interface AdminBacktestFormPageProps {
  /** Sources that aren't ready are left out. */
  sources: EvaluationSource[];
  /** Preselects a source, e.g. when coming from its page. */
  initialSourceId?: string;
  onSubmit: (request: CreateBacktestRequest) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  /** Why the last submit failed. */
  errorMessage?: string;
  /** The form waits for the sources, so a preselected one is found. */
  isLoading?: boolean;
}

const BacktestForm = ({
  sources,
  initialSourceId,
  onSubmit,
  onCancel,
  isSubmitting = false,
  errorMessage,
}: Omit<AdminBacktestFormPageProps, 'isLoading'>) => {
  const readySources = sources.filter((source) => source.status === 'ready');
  const initialSource = readySources.find(({ id }) => id === initialSourceId);
  const initialTranscript = readyTranscripts(initialSource)[0];

  const [sourceId, setSourceId] = useState(initialSource?.id ?? '');
  const [transcript, setTranscript] = useState<BacktestTranscriptChoice>(
    initialTranscript ? 'chosen' : 'fresh',
  );
  const [transcriptId, setTranscriptId] = useState(initialTranscript?.id ?? '');
  const [isTouched, setIsTouched] = useState(false);

  const source = readySources.find(({ id }) => id === sourceId);
  const transcripts = readyTranscripts(source);
  const isTranscriptValid = transcript === 'fresh' || transcriptId !== '';

  const handleSourceChange = (id: string) => {
    const latest = readyTranscripts(readySources.find((s) => s.id === id))[0];
    setSourceId(id);
    setTranscript(latest ? 'chosen' : 'fresh');
    setTranscriptId(latest?.id ?? '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsTouched(true);
    if (!source || !isTranscriptValid) return;
    onSubmit({
      sourceId: source.id,
      transcript,
      ...(transcript === 'chosen' && { transcriptId }),
    });
  };

  return (
    <Box component='form' onSubmit={handleSubmit} noValidate>
      <Stack spacing={3}>
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
        <TextField
          select
          label='Source'
          value={sourceId}
          onChange={(e) => handleSourceChange(e.target.value)}
          required
          fullWidth
          disabled={isSubmitting}
          error={isTouched && !source}
          helperText={
            readySources.length === 0
              ? 'No sources are ready yet'
              : isTouched && !source
                ? 'Pick a source'
                : undefined
          }
        >
          {readySources.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {sourceLabel(option)}
            </MenuItem>
          ))}
        </TextField>
        <FormControl disabled={isSubmitting || !source}>
          <FormLabel id='backtest-transcript-label'>Transcript</FormLabel>
          <RadioGroup
            aria-labelledby='backtest-transcript-label'
            value={transcript}
            onChange={(e) =>
              setTranscript(e.target.value as BacktestTranscriptChoice)
            }
          >
            <FormControlLabel
              value='chosen'
              control={<Radio />}
              label='Use one transcript for every run'
              disabled={transcripts.length === 0}
            />
            <FormControlLabel
              value='fresh'
              control={<Radio />}
              label='Transcribe afresh on every run'
            />
          </RadioGroup>
          {source && transcripts.length === 0 && (
            <FormHelperText>This source has no transcripts yet.</FormHelperText>
          )}
        </FormControl>
        {transcript === 'chosen' && transcripts.length > 0 && (
          <TextField
            select
            label='Transcript to use'
            value={transcriptId}
            onChange={(e) => setTranscriptId(e.target.value)}
            required
            fullWidth
            disabled={isSubmitting}
            error={isTouched && !isTranscriptValid}
          >
            {transcripts.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {transcriptLabel(option)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Stack
          spacing={1}
          sx={{ flexDirection: { xs: 'column-reverse', sm: 'row' } }}
          useFlexGap
        >
          <Button variant='outlined' onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type='submit' variant='contained' disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create backtest'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export const AdminBacktestFormPage = ({
  isLoading = false,
  ...formProps
}: AdminBacktestFormPageProps) => (
  <ModalPageTemplate onBack={formProps.onCancel}>
    <PageTemplate
      maxWidth='sm'
      headerSlot={
        <PageHeader
          title='New backtest'
          description='Pick a source and the transcript to extract from. Each run compares the extraction with the answers the source expects.'
        />
      }
      contentSlot={
        isLoading ? (
          <CircularProgress size={24} />
        ) : (
          <BacktestForm {...formProps} />
        )
      }
    />
  </ModalPageTemplate>
);

AdminBacktestFormPage.displayName = 'AdminBacktestFormPage';
export default AdminBacktestFormPage;
